import { ImgHTMLAttributes, ReactNode, useEffect, useRef, useState } from "react";

import { apiClient } from "src/api/client";


type ProtectedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src?: string | null;
  fallback?: ReactNode;
  alt: string;
};

export const ProtectedImage = ({
  src,
  fallback = null,
  alt,
  onError,
  ...imageProps
}: ProtectedImageProps) => {
  const [resolvedSource, setResolvedSource] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) window.URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (objectUrlRef.current) {
      window.URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setFailed(false);
    setResolvedSource(null);

    if (!src) return;

    if (!apiClient.isProtectedMediaSource(src)) {
      setResolvedSource(src);
      return;
    }

    apiClient
      .getProtectedMedia(src)
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = window.URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setResolvedSource(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!resolvedSource || failed) return <>{fallback}</>;

  return (
    <img
      {...imageProps}
      src={resolvedSource}
      alt={alt}
      onError={(event) => {
        setFailed(true);
        onError?.(event);
      }}
    />
  );
};
